import React from "react";
import { Share2, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { WIN_MESSAGES, LOSE_MESSAGE } from "./rules";

const EMOJI = { correct: "🟩", present: "🟨", absent: "⬛" };

function formatNumber(str) {
  return Number(str).toLocaleString("en-US");
}

function StatCell({ value, label, testid }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl font-bold tabular-nums" data-testid={testid}>
        {value}
      </span>
      <span className="mt-0.5 text-[10px] uppercase tracking-wider text-[var(--numex-muted)] text-center leading-tight">
        {label}
      </span>
    </div>
  );
}

function Distribution({ distribution, highlight }) {
  const values = [1, 2, 3, 4, 5].map((n) => distribution?.[n] ?? distribution?.[String(n)] ?? 0);
  const max = Math.max(1, ...values);
  return (
    <div className="space-y-1.5" data-testid="guess-distribution">
      {values.map((v, i) => {
        const n = i + 1;
        const isHi = highlight === n;
        const pct = Math.max(8, Math.round((v / max) * 100));
        return (
          <div key={n} className="flex items-center gap-2 text-sm">
            <span className="w-3 tabular-nums text-[var(--numex-muted)]">{n}</span>
            <div className="flex-1">
              <div
                className="flex items-center justify-end rounded-[3px] px-2 py-0.5 text-xs font-semibold text-white transition-all"
                style={{
                  width: `${pct}%`,
                  background: isHi ? "var(--numex-green)" : "#bdbdb6",
                }}
                data-testid={`dist-bar-${n}`}
              >
                {v}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Countdown() {
  const [label, setLabel] = React.useState("00:00:00");
  React.useEffect(() => {
    const tick = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      let s = Math.max(0, Math.floor((midnight - now) / 1000));
      const h = String(Math.floor(s / 3600)).padStart(2, "0");
      s %= 3600;
      const m = String(Math.floor(s / 60)).padStart(2, "0");
      const sec = String(s % 60).padStart(2, "0");
      setLabel(`${h}:${m}:${sec}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex flex-col items-center">
      <span className="text-[10px] uppercase tracking-widest text-[var(--numex-muted)]">
        Next NUMEX
      </span>
      <span className="mt-0.5 font-mono text-2xl font-semibold tabular-nums" data-testid="countdown">
        {label}
      </span>
    </div>
  );
}

export default function ResultModal({
  open,
  onOpenChange,
  status,
  puzzleNumber,
  divisor,
  attempts,
  maxAttempts,
  answer,
  guesses,
  stats,
}) {
  const [copied, setCopied] = React.useState(false);
  const finished = status === "won" || status === "lost";
  const scoreLabel = status === "won" ? `${attempts}/${maxAttempts}` : `X/${maxAttempts}`;

  const buildShareText = React.useCallback(() => {
    const rows = guesses
      .map((g) => g.feedback.map((f) => EMOJI[f]).join(""))
      .join("\n");
    return `NUMEX #${puzzleNumber} ${scoreLabel}\n÷${divisor}\n${rows}\nplaynumex.com`;
  }, [guesses, puzzleNumber, scoreLabel, divisor]);

  const handleShare = React.useCallback(async () => {
    const text = buildShareText();
    try {
      if (navigator.share) {
        await navigator.share({ text });
        return;
      }
    } catch (e) {
      /* fall through to clipboard */
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      /* ignore */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }, [buildShareText]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm rounded-xl max-h-[90vh] overflow-y-auto"
        data-testid="result-modal"
      >
        <DialogHeader>
          {finished ? (
            <>
              <DialogTitle className="text-3xl font-extrabold tracking-tight" data-testid="result-heading">
                {status === "won" ? WIN_MESSAGES[attempts] : LOSE_MESSAGE}
              </DialogTitle>
              <DialogDescription className="text-sm text-[var(--numex-muted)]">
                NUMEX #{puzzleNumber} — {scoreLabel} · ÷{divisor}
              </DialogDescription>
            </>
          ) : (
            <>
              <DialogTitle className="text-2xl font-bold tracking-tight">Statistics</DialogTitle>
              <DialogDescription className="text-sm text-[var(--numex-muted)]">
                NUMEX #{puzzleNumber} · ÷{divisor}
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        <div className="space-y-5 pt-1">
          {status === "lost" && answer && (
            <p className="text-sm text-[var(--numex-ink)]" data-testid="reveal-answer">
              Today&apos;s number was{" "}
              <span className="font-mono font-semibold">{formatNumber(answer)}</span>
            </p>
          )}

          {/* Statistics */}
          <div className="grid grid-cols-4 gap-2">
            <StatCell value={stats?.played ?? 0} label="Played" testid="stat-played" />
            <StatCell value={stats?.win_pct ?? 0} label="Win %" testid="stat-winpct" />
            <StatCell value={stats?.current_streak ?? 0} label="Current Streak" testid="stat-streak" />
            <StatCell value={stats?.best_streak ?? 0} label="Best Streak" testid="stat-best" />
          </div>

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--numex-muted)]">
              Guess Distribution
            </p>
            <Distribution
              distribution={stats?.distribution}
              highlight={status === "won" ? attempts : null}
            />
          </div>

          {finished && (
            <button
              type="button"
              onClick={handleShare}
              data-testid="share-btn"
              className="numex-cta flex w-full items-center justify-center gap-2 rounded-lg py-3 text-sm font-semibold tracking-widest"
            >
              {copied ? (
                <>
                  <Check size={16} /> RESULT COPIED
                </>
              ) : (
                <>
                  <Share2 size={16} /> SHARE RESULT
                </>
              )}
            </button>
          )}

          <div className="flex justify-center border-t border-[var(--numex-tile-idle-border)] pt-4">
            <Countdown />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
